/* S19b: PDP 本体の自前 UI（placeholder トグル・wishlist・sale ガード・予約 CTA・追従バー）。
 *
 * ※ ギャラリー切替と動画モーダルは 7-14（Q44＋0713-#23）で assets/tol-pdp-gallery.js へ移設
 *    （メインスライダー・サムネ連動・拡大/動画モーダル。本ファイルは commerce 隣接 UI に専念）。
 *
 * 責務（commerce には一切干渉しない）:
 *  1) placeholder トグル（定期便 / 梱包）= 見た目のみ（フラグ ON で出る placeholder の選択演出）。
 *     実機能は持たない（Phase5 アプリ結合）。Dawn の variant 解決 / 価格 / 在庫 / カート追加には触れない。
 *  3) 店舗在庫ドロワー（#25・DECISIONS 2026-10-05）= sub-actions 左セルの button で <dialog> を開き、Omni Hub の
 *     アプリプロキシへ在庫照会を POST する。判定・文言はサーバー（tol-pdp-store-stock.liquid）。本体の variant は
 *     Dawn の variant-selects へ change を出して動かす（DOM を直接書き換えない）。
 *  2) お気に入りトグル（案E・wishlist-king/Swish 実連携・DECISIONS 2026-06-29）= 実機能。sub-actions 右セルの
 *     [data-tol-wishlist] を WishlistKing グローバル API（addWishlistItem/removeWishlistItem・loadWishlist で
 *     variantId 突合トグル・state:refetch 購読で reflect）に接続。これは placeholder でなく実登録だが、commerce
 *     中核（variant 解決 / 価格 / 在庫 / カート）には干渉しない（Dawn 機構と分離は維持）。
 *
 * ★ Dawn 機構との分離（Copilot 決定1・load-bearing）:
 *  variant 切替・価格 / 在庫更新・URL 更新・カート追加は Dawn の product-info.js / product-form.js が
 *  <product-info> / <variant-selects> / <product-form> / <quantity-input> で処理する。本 JS はそれらに
 *  addEventListener しない・DOM を触らない。掴むのは tol- 自前要素（placeholder・wishlist・バー/シート）のみ。
 *
 * 規律（tol-tabs.js / tol-ugc-modal.js 前例）: custom element 作らない・アロー IIFE +
 *  window.__tolPdpFormLoaded ガード + data-*-init ガード + AbortController teardown +
 *  shopify:section:load/unload 対応。
 */
(() => {
  if (window.__tolPdpFormLoaded) return;
  window.__tolPdpFormLoaded = true;

  /* ---- placeholder トグル（定期便 / 梱包・見た目のみ） ---- */

  const initSubscription = (scope, signal) => {
    const box = scope.querySelector('[data-tol-sub-box]');
    if (!box) return;
    const frame = box.querySelector('[data-tol-sub-frame]');
    const toggle = box.querySelector('[data-tol-sub-toggle]');
    const radio = box.querySelector('[data-tol-sub-radio]');
    if (!toggle) return;
    toggle.addEventListener('click', () => {
      const on = toggle.getAttribute('aria-pressed') !== 'true';
      toggle.setAttribute('aria-pressed', on ? 'true' : 'false');
      if (radio) radio.classList.toggle('is-on', on);
      if (frame) frame.classList.toggle('is-selected', on);
    }, { signal });
  };

  const initPackaging = (scope, signal) => {
    const pack = scope.querySelector('[data-tol-pack]');
    if (!pack) return;
    const cards = Array.from(pack.querySelectorAll('[data-tol-pack-card]'));
    cards.forEach((card) => {
      card.addEventListener('click', () => {
        cards.forEach((c) => {
          c.classList.remove('is-selected');
          c.setAttribute('aria-pressed', 'false');
        });
        card.classList.add('is-selected');
        card.setAttribute('aria-pressed', 'true');
      }, { signal });
    });
  };

  /* ---- お気に入り（案E・wishlist-king/Swish 実連携・実機能） ----
   * sub-actions 右セルの自前 button を WishlistKing グローバル API に接続する。
   * - 識別: variantId（.product-variant-id を都度読む＝variant 追従）。DEV プローブで PRODUCT モードでも
   *   登録の同一性は variantId 単位（同一 handle でも variant 違いは別エントリ・clear 完了後の厳密再現で確証）と
   *   判明したため、判定・add・remove すべて variantId を主キーにする（handle は add の必須引数としてのみ使う）。
   * - トグル: loadWishlist の items から現 variantId 一致 item を探し、あれば removeWishlistItem({wishlistItemId})、
   *   なければ addWishlistItem({productHandle, variantId})。API は冪等でない（重複 add しうる）ため登録判定してから呼ぶ。
   * - reflect: aria-pressed + .is-active（CSS ハート塗り）+ ラベル文言を状態に同期。
   *   外部変更（wishlist ページ/別タブ等）は events.subscribe('state:refetch') で追従（DEV プローブで確認）。
   * - fail-safe（誤操作防止・二系統レビュー反映）:
   *   (1) WishlistKing は App embed の LAZY ロードで遅延・未ロードのことがある。未ロード時は button を hidden 化
   *       （死んだボタンを SR/キーボードに有効と誤認させない・M-2 の精神）。到来後に表示し初期 reflect。
   *   (2) variant 未解決（variantId=null）では登録判定も add もできないので操作しない（handle だけの誤エントリを作らない）。
   *   (3) 登録状態の read（loadWishlist）が失敗したら add/remove しない（read 失敗を「未登録」と誤認して誤 add するのを防ぐ
   *       ＝Copilot M-2）。read の戻りは { ok, item } で「失敗」と「未登録」を区別する。
   * - ★アプリ依存リスク（受容・[[live-impl-detection-needs-runtime]]）: 状態取得は WK._apis.wishlist.loadWishlist という
   *   非公開（_）面に依存する。公開面には wishlist 取得 API が存在しない（DEV プローブで確認＝addWishlistItem 等の
   *   write のみ列挙される）。observeWishlist は WK._state（＝WK.state・メソッドはプロトタイプ上で Object.keys に出ない）
   *   にある（2026-10-01 実測）。Swish の将来更新で
   *   消失/改名しうるが、失敗時は (3) で「操作しない」に倒れる（誤データを書かない）＝fail-safe。add/remove は公開 API を使う。
   * - ★書き込みロック（ds04-#2・2026-10-01 実測・公式ドキュメントでは未確認）: add/remove は内部ロック中なら undefined を
   *   返して何もしない。ロックは書き込みで立ち、解除は WK.state.observeWishlist の購読ハンドラだけ＝購読者がいないと
   *   1 回書き込んだページでは二度と外れない（3 回目から色が変わらない不具合の原因）。→ ensureObserver で 1 本張り、
   *   戻り値が空なら「効かなかった」と扱って解除を待って 1 回だけやり直す。tol-wishlist-card.js と同じ関数・window で共有。 */
  const ensureObserver = (WK) => {
    const st = WK && WK.state;
    if (!st || typeof st.observeWishlist !== 'function') return null;
    const cur = window.__tolWishlistObserver;
    if (cur && cur.state === st) return cur;
    try {
      const hub = { state: st, waiters: [] };
      st.observeWishlist({ wishlistId: 'mine' }).subscribe(() => {
        const ws = hub.waiters.splice(0);
        setTimeout(() => ws.forEach((w) => w()), 0); /* ロック解除は同じ callback の直後＝1 tick 遅らせる */
      });
      window.__tolWishlistObserver = hub;
      return hub;
    } catch (_) {
      return null;
    }
  };

  /* 購読の次の通知（＝ロック解除）を最大 ms 待つ。true = 通知あり／false = 時間切れ。 */
  const waitRelease = (hub, ms) => new Promise((resolve) => {
    let timer = 0;
    const waiter = () => { clearTimeout(timer); resolve(true); };
    timer = setTimeout(() => {
      const i = hub.waiters.indexOf(waiter); /* 時間切れの待ち手は共有の配列から外す（溜めない） */
      if (i !== -1) hub.waiters.splice(i, 1);
      resolve(false);
    }, ms);
    hub.waiters.push(waiter);
  });

  const initWishlist = (scope, signal) => {
    const btn = scope.querySelector('[data-tol-wishlist]');
    if (!btn) return; /* show_wishlist OFF */
    const handle = btn.getAttribute('data-product-handle');
    const label = btn.querySelector('[data-tol-wishlist-label]');
    if (!handle) return;

    /* WishlistKing が必要な公開 API を備えているか（partial hydrate / バージョン差対策・m-5）。 */
    const isReady = (WK) => !!WK
      && typeof WK.addWishlistItem === 'function'
      && typeof WK.removeWishlistItem === 'function';

    /* 現在選択中 variant id（Dawn が hidden input を書換える。未解決時は null）。number で返す。 */
    const currentVariantId = () => {
      const v = scope.querySelector('.product-variant-id');
      const n = v && v.value ? parseInt(v.value, 10) : NaN;
      return Number.isFinite(n) ? n : null;
    };

    const reflect = (on) => {
      btn.setAttribute('aria-pressed', on ? 'true' : 'false');
      btn.classList.toggle('is-active', on);
      if (label) label.textContent = on ? 'お気に入り済み' : 'お気に入りに追加';
    };

    /* 現 variant の登録 item を読む。戻り { ok, item }: ok=false は read 失敗（状態不明）・
     * ok=true & item=null は未登録・ok=true & item は登録済み。variantId 未解決なら ok=false（判定不能）。 */
    const readItem = async (WK, variantId = currentVariantId()) => {
      if (variantId == null) return { ok: false, item: null };
      try {
        const res = await WK._apis.wishlist.loadWishlist({ wishlistId: 'mine' });
        const items = (res && res.wishlist && res.wishlist.items) || [];
        /* it.variantId は number（DEV プローブ確認）。currentVariantId も number。混在突合はしない（誤 remove 回避）。 */
        const item = items.find((it) => it.variantId === variantId) || null;
        return { ok: true, item };
      } catch (_) {
        return { ok: false, item: null };
      }
    };

    /* 1 回分のトグル（variantId はクリック時に固定）。戻り { result, was }: result = 書き込み後の登録状態
     * （true/false）／UNKNOWN（read 失敗＝何もしない）／SKIPPED（Swish が書き込みを受け付けなかった＝ロック中など）。
     * was = 書き込み前に読んだ登録状態。expectWas を渡したとき（やり直し）は、読み直した状態が 1 回目に読んだ状態と
     * 同じときだけ書き込む（違えば書き込まずにその状態を返す＝クリックの意図と逆の書き込みをしない）。 */
    const UNKNOWN = 'unknown';
    const SKIPPED = 'skipped';
    const toggleOnce = async (WK, variantId, expectWas) => {
      const { ok, item } = await readItem(WK, variantId);
      if (!ok) return { result: UNKNOWN, was: null }; /* fail-safe(3): 状態不明なら add/remove しない（誤 add/remove 防止） */
      const was = !!item;
      if (expectWas !== undefined && was !== expectWas) return { result: was, was };
      const res = item
        ? await WK.removeWishlistItem({ wishlistItemId: item.id })
        : await WK.addWishlistItem({ productHandle: handle, variantId: variantId });
      if (!res) return { result: SKIPPED, was };
      return { result: !was, was, wrote: true };
    };

    let busy = false;
    /* クリックの世代。読み直し（resync / sync）は読み始めた世代のままのときだけ反映する＝読みの最中に
     * 次のクリックが始まって成功しても、古い読みの結果で上書きしない（Codex 実 diff P2）。 */
    let gen = 0;
    /* busy 中に飛ばした sync（variantChange／state:refetch）があるか。busy が外れたら現 variant を読み直す
     * ＝書き込み中に variant を A→B→A と戻しても A の表示が古いまま残らない（Codex 実 diff 2 回目 P2）。 */
    let pendingSync = false;

    /* variantId の登録状態を読み、その variant が今も選択中で、かつ次のクリックが始まっていないときだけ
     * 表示へ反映する（読み直しの最中に variant が切り替わったら旧 variant の結果を映さない）。read も失敗なら現表示維持。 */
    const resync = async (variantId) => {
      const WK = window.WishlistKing;
      if (!isReady(WK) || variantId == null) return;
      const g = gen;
      const r = await readItem(WK, variantId);
      if (r.ok && g === gen && currentVariantId() === variantId) reflect(!!r.item);
    };

    btn.addEventListener('click', async () => {
      const WK = window.WishlistKing;
      if (!isReady(WK) || busy) return; /* fail-safe(1): 未ロード/未対応は無反応（hidden 化済みのはず） */
      const variantId = currentVariantId();
      if (variantId == null) return; /* fail-safe(2): variant 未解決では操作しない */
      busy = true;
      gen += 1;
      const same = () => currentVariantId() === variantId;
      let shown = false; /* このクリックの結果を表示へ反映できたか */
      let released = true; /* 自分の書き込みの後、Swish の再読み込み（キャッシュ更新）の完了を確認できたか */
      let hub = null;
      try {
        hub = ensureObserver(WK);
        let r = await toggleOnce(WK, variantId);
        /* ロック中に当たったら busy のまま解除を待って 1 回だけやり直す（ds04-#2）。通知が来ず時間切れでも 1 回は
           確かめる（読み直しが 1 回目と同じときだけ書く）。variant が変わっていたらやり直さない。 */
        if (r.result === SKIPPED && hub && same()) {
          await waitRelease(hub, 3000);
          if (same()) r = await toggleOnce(window.WishlistKing, variantId, r.was);
        }
        if (r.result === SKIPPED) await resync(variantId); /* 効かなかった＝真の状態へ（嘘の表示をしない） */
        else if (r.result !== UNKNOWN && same()) { reflect(r.result); shown = true; }
        /* 自分の書き込みが効いたら、Swish の後始末（再読み込み＝ロック解除とキャッシュ更新）が終わるまで busy を保つ
           （直後のクリックが書き込み前のキャッシュを読んで利用者の操作を失うのを防ぐ・tol-wishlist-card.js と同じ）。 */
        if (r.wrote && hub) released = await waitRelease(hub, 3000);
      } catch (_) {
        await resync(variantId); /* write 失敗: 楽観更新の巻き戻し */
      } finally {
        busy = false;
        /* 遅れて呼ばれた時点で次のクリックが進行中なら、そのクリックの終わりに任せる。 */
        const later = () => {
          if (busy) { pendingSync = true; return; }
          pendingSync = false;
          resync(currentVariantId());
        };
        /* busy 中は variantChange／state:refetch の sync がスキップされる＝クリック中に variant が変わっていた・
           sync を飛ばしていた・このクリックの結果を表示へ反映できなかった（書き込みの完了時に別 variant が選ばれて
           いて、その後に戻った等。variantChange の sync は rAF 経由で遅れうる）なら、現 variant を読み直す。 */
        /* 再読み込みの完了を確認できないまま（3 秒の時間切れ）読むと書き込み前のキャッシュを映しうる＝その場合は
           完了の通知まで読み直しを遅らせる（Codex 実 diff 3 回目 P2）。通知が来なければ表示はクリックの結果のまま。 */
        if (pendingSync || !same() || !shown) {
          if (released || !hub) later();
          else hub.waiters.push(later);
        }
      }
    }, { signal });

    /* button の出し入れ（L-4・Copilot m-1）: variant が解決できない（完全売り切れで
     * selected_or_first_available_variant=nil 等）と click が無反応になるため、その間は button を
     * hidden にする＝inert なボタンを SR/キーボードに見せない＋2 列 grid に空セルを残さない
     * （CSS の :has([data-tol-wishlist][hidden]) が grid を 1 列へ畳む）。variant 解決済みなら表示。 */
    const applyAvailability = () => {
      const unavailable = currentVariantId() == null;
      if (unavailable && btn.getAttribute('aria-pressed') !== 'true') btn.hidden = true;
      else if (!unavailable) btn.hidden = false;
    };

    /* WishlistKing 到来後に初期 reflect + 外部変更購読をセットする。 */
    const wire = async (WK) => {
      /* click 中（busy）の sync は楽観更新を旧状態で上書きしうる（m-3）→ busy 中はスキップ。 */
      const sync = async () => {
        if (busy) { pendingSync = true; return; }
        const g = gen;
        const v = currentVariantId();
        const { ok, item } = await readItem(WK, v);
        /* read 失敗時は表示を変えない（未登録に誤誘導しない）。読みの最中にクリックが始まった・variant が
           変わった場合も古い結果を映さない（クリック側の reflect と後続の state:refetch が正しい状態を出す）。 */
        if (ok && !busy && g === gen && currentVariantId() === v) reflect(!!item);
      };
      /* フラッシュ回避（m-2）: hidden のまま初期状態を確定してから表示する。 */
      await sync();
      applyAvailability(); /* variant 未解決なら hidden 維持・解決済みなら表示 */
      try {
        if (WK.events && typeof WK.events.subscribe === 'function') {
          /* state:refetch は add/remove/外部変更で発火（DEV プローブ確認）。loadWishlist は read なので
           * 自分の add/remove で発火しても再発火ループにはならない（DEV プローブ確認）。解除関数があれば abort 時に呼ぶ。 */
          const off = WK.events.subscribe('state:refetch', sync);
          if (typeof off === 'function') signal.addEventListener('abort', off, { once: true });
        }
      } catch (_) { /* イベント購読不可でも click 時 reflect で機能は保たれる */ }
      /* variant 切替に追従して availability/状態を更新（initSaleGuard と同じ variantChange 購読・自セクション限定）。 */
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        const unsub = subscribe(PUB_SUB_EVENTS.variantChange, (payload) => {
          if (payload?.data?.sectionId && payload.data.sectionId !== scope.dataset.section) return;
          requestAnimationFrame(() => { applyAvailability(); sync(); });
        });
        if (typeof unsub === 'function') signal.addEventListener('abort', unsub, { once: true });
      }
    };

    if (isReady(window.WishlistKing)) {
      wire(window.WishlistKing);
    } else {
      /* LAZY ロード待ち: 未ロードの間は hidden（死んだボタンを出さない）。短いポーリングで到来を待ち、
       * 一定時間来なければ hidden のまま据え置く（App embed 無効 = 掲載しないのと同義の fail-safe）。 */
      btn.hidden = true;
      let tries = 0;
      const timer = setInterval(() => {
        tries += 1;
        if (isReady(window.WishlistKing)) { clearInterval(timer); wire(window.WishlistKing); }
        else if (tries >= 40) { clearInterval(timer); } /* 約10秒で諦め（hidden 維持） */
      }, 250);
      signal.addEventListener('abort', () => clearInterval(timer), { once: true });
    }
  };

  /* ---- 起動 ---- */

  /* ---- T5 sale ガード（inert 非サポート環境のフォールバックのみ・二系統レビュー H-1/M-1） ----
   * 通常は購入不可をサーバーレンダ（buy-buttons 非 render・自前 disabled+aria-disabled ボタン）＋
   * variant ラッパー inert で閉じる。よって sale 期間外は variant 切替自体が起きず、このガードは作動しない。
   * ただし inert 非サポート環境（古い Safari/WebView 等）では variant 切替が発火しうる。その場合
   * Dawn product-info.js:199-201 が再フェッチ HTML の disabled で submit を再設定し文言を soldOut（売り切れ）に
   * 上書きする＝sale ラベルが「売り切れ」に潰れ、かつ finally で aria-disabled が剥がれて購入可能に戻る恐れ。
   * そこで variantChange（product-info.js が publish）を購読し、sale 期間外のときに限り submit へ
   * disabled+aria-disabled と sale ボタン文言を再適用する。on（通常販売）では何もしない（commerce 非干渉維持）。 */
  const initSaleGuard = (pdp) => {
    const state = pdp.dataset.tolSaleState;
    if (state !== 'pre' && state !== 'ended') return; /* 通常販売は対象外 */
    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;

    const reapply = () => {
      const btn = pdp.querySelector('.product-form__submit');
      if (!btn) return;
      btn.setAttribute('disabled', 'disabled');
      btn.setAttribute('aria-disabled', 'true');
      const span = btn.querySelector('span');
      const label = pdp.dataset.tolSaleBtnLabel;
      if (span && label) span.textContent = label;
    };

    const unsub = subscribe(PUB_SUB_EVENTS.variantChange, (payload) => {
      /* 自セクション以外の variantChange は無視（複数 PDP/quick-add 共存時の取り違え防止）。 */
      if (payload?.data?.sectionId && payload.data.sectionId !== pdp.dataset.section) return;
      /* product-info.js の DOM 反映（toggleSubmitButton）後に再適用するため次フレームへ。 */
      requestAnimationFrame(reapply);
    });
    pdp._tolSaleUnsub = unsub;
  };

  /* ---- R12 層 (c) カート手前制御のラベル復旧ガード（通常 PDP・initSaleGuard と同型） ----
   * 抑止そのものはサーバーが持つ（sections/tol-main-product.liquid で buy-buttons を render しない）。
   * ただし variant 切替が起きると product-info.js:199-202 が
   *   toggleSubmitButton(fetchHTML の disabled 有無, window.variantStrings.soldOut)
   * を呼び、product-form.js:127-131 が submitButtonText を「売り切れ」に書き換える＝理由を誤って伝える。
   * disabled は fetch HTML から復元されるので抑止は保たれる。ここではラベルだけ書き戻す。
   * ★ 初期ロード時に発火しないのは意図どおり（variantChange は optionValueSelectionChange 起点＝ユーザーが
   *   ピッカーを操作したときだけ）。初期状態はサーバーが正しく描いている。 */
  const initCartGuardLabel = (pdp) => {
    if (pdp.dataset.tolCartGuardBlocked !== 'true') return;
    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;

    const label = pdp.dataset.tolCartGuardLabel;
    if (!label) return;

    const reapply = () => {
      const btn = pdp.querySelector('.product-form__submit');
      if (!btn) return;
      /* disabled と aria-disabled は常に同時操作する（片方だけだと中間状態＝drift・applyCtaState の規律）。 */
      btn.setAttribute('disabled', 'disabled');
      btn.setAttribute('aria-disabled', 'true');
      const span = btn.querySelector('span');
      if (span) span.textContent = label;
    };

    const unsub = subscribe(PUB_SUB_EVENTS.variantChange, (payload) => {
      try {
        /* 自セクション以外の variantChange は無視（複数 PDP / quick-add 共存時の取り違え防止）。 */
        if (payload?.data?.sectionId && payload.data.sectionId !== pdp.dataset.section) return;
        /* product-info.js の DOM 反映（toggleSubmitButton）後に再適用するため次フレームへ。 */
        requestAnimationFrame(reapply);
      } catch (e) {
        /* pubsub は購読 callback の例外を隔離しない（後述 cartUnsub と同じ理由）。 */
        console.error('[tol-pdp-form] cart guard label reapply で例外', e);
      }
    });
    pdp._tolCartGuardUnsub = unsub;
  };

  /* ---- W5 予約 CTA の enable owner（ワークショップ別 PDP・product.kouza-workshop のみ） ----
   * ＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝
   * 正本: docs/product-master/workshop-pdp-rebuild-design.md（W5）・DECISIONS 2026-07-01 WS ADR・
   *   ~/.claude/plans/product-kouza-workshop-w5-cta-virtual-swan.md（本実装の設計プラン・二系統レビュー反映）。
   *
   * kouza カレンダー（tol-product-kouza.js）は日時確定で kouza:variant-confirmed（detail: {variantId, variant,
   * reservable}）、クリアで kouza:variant-cleared を dispatch する（submit には触らない）。W5 はこの owner として
   * 自前 submit（buy-buttons 非 render・T5 sale 同型）の enable/disable/文言を制御する。
   *
   * ★ 設計の核心（状態評価駆動・DOM を真実源にしない）: submit の状態は「イベントが起きた」で駆動せず、
   *   applyCtaState(enabled) 単一導出関数で毎回 DOM に反映する。イベント（confirmed/cleared/variantChange）は
   *   「導出を再実行するトリガ」にすぎない。予約可否の権威判定は kouza が detail.reservable に載せた boolean を
   *   信じる（isReservable を owner 側で再導出しない＝2ファイル複製の drift＝fail-open を排除・二系統レビュー HIGH-2）。
   *
   * ★ fail-closed: reservation_enabled=false（受付停止）では非初期化（enable owner を張らない＝kouza init ガードと
   *   同条件で自己抑止）。submit を触るのは applyCtaState のみ（唯一の書き込み口＝不変条件）。fail-closed の一次保証は
   *   サーバーレンダの初期 disabled+aria-disabled で、この JS enable は「選択が有効なときだけ」の best-effort レイヤー。
   *
   * ★ テーマ層 best-effort の限界（launch-blockers 7-10 同一射程）: form.submit()/直 fetch のプログラム的 POST は
   *   塞がない。WS 特有の Shopify Function は起票しない。
   * ★ 「¥0 なので金銭被害は低い」は workshop 限定の前提（W0 A3）。R15 T0（2026-09-08）で講座（kouza・有償・最大 ¥176,020）が
   *   同じ owner の母集団に入った＝二重投入の保護は下記 cartUnsub（二重予約ガード）と R12 層 (d) の数量ロックが load-bearing。
   *   層 (d) はタグ付きの商品だけ。4b（DECISIONS 2026-10-05（3））以後のタグ無しの有償イベントは cartUnsub だけ（層 (d) と同意のゲートが無いのは LIVE と同じ）。
   * ★ R15 T0 direct モード（Default Title 1 件の講座）: kouza JS が初期化時に kouza:variant-confirmed を 1 回 dispatch する。
   *   dispatch は owner 装着後でないと捨てられる（script の defer 文書順 = tol-pdp-form.js → tol-product-kouza.js が契約）。
   *   保険として kouza は dispatch 前に pdp._tolKouzaDirect = {variantId, reservable} を置き、owner は装着時にそれを
   *   acceptConfirmed に通す（イベント経路と同じ関数で lastReservable と DOM を同時更新＝順序が崩れても復帰）。 */
  const initReservationCta = (pdp, scope, signal) => {
    /* reservation_enabled=false（不正 variant 構成・受付停止。4b でタグは見なくなった）では enable owner を張らない。
     * 発火源（kouza）も同条件で非初期化＝confirmed が dispatch されない＝二重ガード。 */
    if (pdp.dataset.tolKouzaEnabled !== 'true') return;
    /* ★ R12 層 (c)（LB 5-10・DECISIONS 2026-08-31 決定 1）: カート手前制御でブロック中は enable owner を
     * 張らない ⇒ サーバーレンダの初期 disabled+aria-disabled が最後まで残る（＝ガードが勝つ）。
     * 上の reservation_enabled ガードと同じ機構なので、新しい優先順位ルールを持ち込まない。
     * ★ data-tol-kouza-enabled を false にする案は却下（カレンダーごと消え、文言が「ただいま予約を
     *   受け付けていません」になって理由を誤って伝える）。 */
    if (pdp.dataset.tolCartGuardBlocked === 'true') return;
    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;

    const form = scope.querySelector('[data-tol-kouza-form]');
    if (!form) return;
    const submit = scope.querySelector('.product-form__submit');
    const idInput = form.querySelector('[name="id"]');
    if (!submit || !idInput) return;
    /* ラベルは JS が locale を読めないため product-form の data 属性から取る（section 側で | t 済み・W2 の
     * data-i18n-* と同じ制約対応）。span は submit 内の最初の span（loading-spinner は SVG のみで span を持たない）。 */
    const span = submit.querySelector('span');
    const reserveLabel = form.dataset.reserveLabel || '';
    const selectLabel = form.dataset.selectLabel || '';

    /* ★ submit の disabled/aria-disabled/span を書き換える唯一の書き込み口（不変条件・状態評価駆動の前提）。
     * disabled と aria-disabled を常に同時操作する（片方だけだと「見た目 enable だが submit ゲートは効いたまま」等の
     * 中間状態＝drift。aria-disabled は product-form.js:22 onSubmitHandler の唯一ゲート・disabled は押下抑止＋CSS グレーアウト）。
     * ★ 唯一の例外＝WS redirect 分岐の native disabled 同期先出し（cartUnsub 内・二重予約ガード）: rAF の applyCtaState(false)
     *   が同フレーム内に必ず追いつき disabled+aria を収束させるため drift は生じない（違反として巻き戻さないこと）。 */
    /* ★ ds04 #99（DECISIONS 2026-10-08（9））: 2 名以上の 422 の後、カートを読み直している間のロック。
     * この間は何が来ても（日程の確定し直し・variantChange の後追い・追加購入の復帰）CTA を有効にしない＝唯一の書き込み口で止める。 */
    let reconciling = false;

    const applyCtaState = (enabled, labelOverride) => {
      if (enabled && reconciling) enabled = false;
      if (enabled) {
        submit.removeAttribute('disabled');
        submit.setAttribute('aria-disabled', 'false');
        if (span && reserveLabel) span.textContent = reserveLabel;
      } else {
        submit.setAttribute('disabled', 'disabled');
        submit.setAttribute('aria-disabled', 'true');
        /* R15 ①: 追加購入の送信中だけ「追加中...」を一時上書き（任意第 2 引数・disabled/aria の同時操作は不変） */
        const offLabel = labelOverride || selectLabel;
        if (span && offLabel) span.textContent = offLabel;
      }
    };

    /* 予約可否 = kouza 権威 boolean（detail.reservable）＋ 実 DOM の [name=id] 非空（二重チェック・fail-closed 側に厚く）。 */
    const canReserve = (detail) => !!(detail && detail.reservable) && idInput.value !== '';

    /* confirmed 時に確定した予約可否をキャッシュ（HIGH-1・variantChange 防御ガードで value 非空の代理近似に
     * 落とさず、confirmed 経路と同一の厳密さ〔isReservable 込み・kouza 権威 boolean 由来〕で再評価するため）。 */
    let lastReservable = false;

    /* ★ R15（Codex クロスレビュー 2026-09-08 BLOCKER）: 送信した variant を「不変値」として持つ。
     * cartUpdate の照合に現在の [name=id]（＝送信中にカレンダーで別日程を確定すると書き換わる）を使うと、
     * 送信した A の cartUpdate が「別 variant」と見なされて無視され、二重予約ガードも /cart 遷移も両方すり抜ける。
     * 有償講座（R15 T0 で 178 件が calendar 経路に乗った）では実損になるため、送信時点の id で照合する。
     * cartCommitted はカート追加が確定した後の終端ラッチ＝遷移が commit されるまでの窓で再 enable させない。 */
    let submittedVariantId = null;
    let cartCommitted = false;

    /* ★ イベント経路と direct の bootstrap 経路を同じ関数に通す（critical MAJOR-3・R15 T0）:
     * DOM だけ enable すると次の variantChange/失敗復帰が lastReservable===false を読んで永久 disabled に戻るため、
     * lastReservable と applyCtaState を必ず同時に更新する。 */
    const acceptConfirmed = (detail) => {
      if (cartCommitted) {
        /* 追加確定後（遷移待ち）は何が来ても閉じたまま＝同一ページからの再追加を作らない。 */
        lastReservable = false;
        applyCtaState(false);
        return;
      }
      lastReservable = canReserve(detail);
      applyCtaState(lastReservable);
    };

    /* すべての submit（Dawn 経路・追加購入経路の両方）で送信 variant を記録する。capture フェーズなので
     * Dawn の onSubmitHandler（form の非 capture）より先に走る。追加購入の割り込み自体は下の別リスナーが持つ。 */
    /* ★ ds04 #99: 人数の送信前の補正もここで行う（追加購入の割り込みより先に登録された capture＝両経路が補正後の値を読む）。
     * 手入力は form が novalidate なので上限を超えたまま送れてしまう。整数・下限 1・上限 min(input.max, data-base-max-qty)
     * （input.max は kouza JS の min(5, quantity_rule.max, 残席)。Dawn の setQuantityBoundries が外したときは base で受ける）。
     * 無効の入力（日程を決める前・売り切れ）は FormData に入らない＝1 名。 */
    const qtyField = scope.querySelector('[data-kouza-qty-root] input[name="quantity"]');
    let submittedQty = 1;
    const clampQty = () => {
      if (!qtyField || qtyField.disabled) return 1;
      const root = qtyField.closest('[data-kouza-qty-root]');
      const base = parseInt(root && root.dataset.baseMaxQty, 10) || 5;
      const maxAttr = parseInt(qtyField.getAttribute('max'), 10);
      const max = Math.max(1, maxAttr > 0 ? Math.min(maxAttr, base) : base);
      let v = parseInt(qtyField.value, 10);
      if (!(v >= 1)) v = 1;
      if (v > max) v = max;
      if (qtyField.value !== String(v)) qtyField.value = String(v);
      return v;
    };
    form.addEventListener('submit', () => {
      /* #99（Codex 実 diff P1）: Dawn が捨てる送信（送信中の 2 回目＝aria-disabled・閉じた CTA）では記録を上書きしない。
         上書きすると、受け付けた 1 回目の 422 の照合が 2 回目の人数（例 1）を読んで素通りする。 */
      if (submit.disabled || submit.getAttribute('aria-disabled') === 'true') return;
      if (idInput.value !== '') submittedVariantId = idInput.value;
      submittedQty = clampQty();
    }, { capture: true, signal });

    pdp.addEventListener('kouza:variant-confirmed', (e) => {
      acceptConfirmed(e.detail);
    }, { signal });

    pdp.addEventListener('kouza:variant-cleared', () => {
      lastReservable = false;
      applyCtaState(false);
    }, { signal });

    /* ★ R15 T0 順序保険: kouza（direct）が owner 装着前に dispatch していた場合、pdp._tolKouzaDirect から復帰する。
     * variantId が実 DOM の [name=id] と一致するときだけ受け入れる（別 variant の stale 値を enable に使わない）。 */
    const bootstrapDirect = pdp._tolKouzaDirect;
    if (bootstrapDirect && idInput.value !== '' && String(bootstrapDirect.variantId) === String(idInput.value)) {
      acceptConfirmed(bootstrapDirect);
    }
    /* 消費したら破棄する（同一ノードを再初期化する経路〔テーマエディター・構造テスト〕で前回の reservable:true を
       次回の owner が読んで stale に enable しないため）。kouza 側も unload で破棄する（二重の安全弁）。 */
    pdp._tolKouzaDirect = null;

    /* variantChange 防御ガード（常設・裁定2）: 経路A（WS に variant-selects 不在）で通常発火しないが、theme editor の
     * 再フェッチ等の異常系で product-info.js:199-201 が toggleSubmitButton(...soldOut) で submit の disabled と span を
     * 触った場合に、rAF 後に applyCtaState で「外部書き込みを上書きする最後の砦」。initSaleGuard は「常に disable」
     * だが W5 は「lastReservable なら enable・そうでなければ disable」の双方向再計算（value 非空の代理をやめる）。 */
    const unsub = subscribe(PUB_SUB_EVENTS.variantChange, (payload) => {
      /* sectionId 一致時のみ処理（sectionId 欠落の publish でも他セクション起因で誤発火しない・厳密側に倒す）。 */
      if (!payload?.data?.sectionId || payload.data.sectionId !== pdp.dataset.section) return;
      requestAnimationFrame(() => applyCtaState(lastReservable && idInput.value !== ''));
    });
    if (typeof unsub === 'function') signal.addEventListener('abort', unsub, { once: true });

    /* ★ 二重予約ガード（DEV 実機 PoC-5 で二重投入を実測＝座席枠の二重計上を確認・二系統レビュー MEDIUM-A 両系統一致）:
     * product-form.js の onSubmitHandler は /cart/add 成功後の finally で aria-disabled を剥がす（:105）ため、
     * WS（正常に予約成功する）では「予約手続きに進む」ボタンが押せたまま残り、再クリックで同一枠を二重に確保できる（WS は ¥0 だが
     * 座席在庫を 2 消費・★R15 T0 以後の講座は有償＝有償 qty 2 になり層 (d) の数量ロックで /cart から減らせない＝本ガードが load-bearing）。cartUpdate を購読し、自 pdp の確定 variant がカート追加されたら submit を再 disable して
     * 再選択するまで再予約させない。正規 UI の誤二重クリックを塞ぐ best-effort（form.submit()/直 fetch は 7-10 射程外・
     * WS 特有 Function 起票なし）。カート追加成功時は lastReservable も false に落とす（下記＝再予約導線を閉じる）。 */
    const cartUnsub = subscribe(PUB_SUB_EVENTS.cartUpdate, (payload) => {
      /* ★ cartUpdate の payload は product-form.js:71 が publish する {source, productVariantId, cartData}＝
       * トップレベル（variantChange の {data:{sectionId,...}} ネストとは構造が違う・pubsub.js:20 は callback(data) で
       * data を直接渡す）。DEV 実機で payload 構造を実測確認済み（payload.source / payload.productVariantId）。 */
      if (!payload || payload.source !== 'product-form') return;
      /* 自 pdp の確定 variant がカート追加されたときだけ再 disable（productVariantId は文字列/数値の揺れを吸収）。
       * idInput.value は比較時点の DOM 値だが、WS 経路A（variant-selects 不在）では確定〜カート追加の間に別 variant へ
       * 変わらない＝実質固定（万一 stale でも lastReservable=false は下で確定するので二重予約は阻止される）。 */
      const added = payload.productVariantId;
      /* ★ 照合は「送信した variant」で行う（Codex BLOCKER 是正）。submittedVariantId が無い異常系のみ
       * 現在値へフォールバックする（従来挙動）。 */
      const expected = submittedVariantId || idInput.value;
      if (added == null || String(added) !== String(expected)) return;
      cartCommitted = true;
      /* lastReservable も false に落とす（variantChange 防御ガードが再 enable しないよう一貫させる＝
       * 予約済み枠は再選択するまで再予約導線を閉じる）。redirect の成否に関わらず先に確定する（critical L-2 の趣旨）。 */
      lastReservable = false;
      /* ★ 二重予約ガード＝submit 再 disable（redirect の“前”に張る・critical B-1 是正）。
       * window.location.href はナビゲーションを“予約”するだけで、新 document が commit される（=/cart 応答が来る・
       * モバイルで数百 ms〜秒）まで現ページは操作可能なまま＝「予約手続きに進む」ボタンが enabled で残ると再タップで座席在庫を
       * 二重消費しうる（ガード新設の根拠だった PoC-5 実測バグの窓）。redirect の後に置くと成功パスで到達せずスキップ
       * されるため、redirect 判定の前でガードを張る。
       * ★ 二段構え（rAF は同期ロックでない・Copilot 再レビュー MEDIUM）: rAF は次フレーム（〜16ms）まで遅延するため、
       *   product-form.js:105 の finally（microtask）が aria-disabled を剥がした後〜rAF 発火までの小窓に素早い再タップが
       *   割り込む余地がある（座席在庫の実害＝fail-safe に倒す）。そこで①native disabled を“同期で先出し”して押下を即抑止し
       *   （onSubmitHandler 到達前にブラウザレベルで submit を止める・:22 の aria-disabled ゲートより手前）②rAF で finally
       *   後に applyCtaState(false)＝disabled+aria-disabled を確定（既存の rAF 後追い＝finally が publish の“後”に
       *   aria-disabled を剥がす順序に対応）。native disabled は applyCtaState の管理下（:361-371 の唯一の書き込み口）と
       *   一貫＝rAF の applyCtaState(false) が同じ disabled を再確認する。
       * ※ redirect 成功時に DOM が既に外れていても setAttribute は silent 失敗で無害。 */
      submit.setAttribute('disabled', 'disabled');
      requestAnimationFrame(() => applyCtaState(false));
      /* ★ WS = カートページ遷移（Q23 ディレクター裁定・DECISIONS 2026-07-13（2）論点 B）。
       * ★ 防御ガード必須（pubsub.js:17-25 は購読 callback の例外を隔離しない＝ここで throw すると同 publish の
       *   後続購読と product-form.js の renderContents 連鎖まで壊す silent break・G6 ADR の try/catch 教訓と同根）:
       *   window.routes.cart_url が非空の文字列のときだけ遷移。プロパティアクセスと typeof は throw しないので try/catch
       *   不要（&& で短絡）。不成立時（window.routes 破損等の異常系のみ・theme.liquid:353-358 で無条件定義）は return
       *   せず上の rAF ガードにフォールバック（fail-safe）＝redirect しないので下記 drawer 抑止もしない。 */
      const cartUrl = window.routes && window.routes.cart_url;
      if (typeof cartUrl === 'string' && cartUrl !== '') {
        /* ★ cart-drawer 抑止（critical B-2 是正）: cart_type=drawer 化後、product-form.js:95 の renderContents→
         * setTimeout（cart-drawer.js:86-89）→ open()（さらに setTimeout で animate active 付与・:33-35）で drawer が
         * スライドインする。location.href は“保留中の setTimeout を破棄しない”（破棄は新 document commit=unload 時）
         * ため、navigation commit（/cart 応答）まで drawer が開いて見える。host（cart-drawer 要素）に display:none を
         * 付けると .active の visibility transition が発火せず transitionend 不発＝open() の trapFocus も走らない
         * （display:none は transition 非発火・W3C）。host は renderContents の差し替え対象（内側 #CartDrawer）の外側
         * なので inline style は生存。redirect 成功が確定した“この分岐内だけ”で付ける（不成立フォールバックでは host に
         * 触れず通常 drawer 表示を壊さない）。この購読は renderContents（product-form.js:95）より前に走る
         * （publish の同期 callback＝pubsub.js:19-21）。 */
        const drawer = document.querySelector('cart-drawer');
        if (drawer) drawer.style.display = 'none';
        /* ★ bfcache 復元の巻き戻し（critical B-3・Copilot 再レビュー BLOCKER の両系統一致）: display:none は open() の
         * JS 実行自体は止めない＝navigation 待ち中に open() が body.overflow-hidden（cart-drawer.js:49・同期・transitionend
         * 外）と host の animate/active（:33-35 setTimeout）を付与する（見えないだけ）。bfcache（iOS Safari で強く engage）は
         * JS ヒープ＋DOM を丸ごと snapshot 復元するので、/cart から「戻る」で ①body.overflow-hidden 残留＝ページスクロール
         * 不能（base.css）②host display:none 永続＝カートアイコン click（cart-drawer.js:16-18）が不可視 drawer を開くだけ＝
         * カート導線が壊れる。テーマに pageshow ハンドラは他に無い（自然回復経路なし）ため、この分岐で pageshow(e.persisted)
         * を張り復元時に状態を巻き戻す。CTA の disabled は snapshot に残す＝戻った直後は再予約不可（望ましい）。
         * ★ R15（critical MAJOR-2）: 終端ラッチ cartCommitted と送信 id もここで解く。解かないと bfcache 復元後に
         *   日程を選び直しても acceptConfirmed が早期 return して CTA が永久に戻らない（講座 279 件・direct 型は
         *   選び直す UI すら無い）。「戻った直後は disabled・再選択で戻せる」という従来の受容へ復帰させる。 */
        window.addEventListener('pageshow', function onPersistedRestore(e) {
          if (!e.persisted) return;
          if (drawer) {
            drawer.style.display = '';
            drawer.classList.remove('active', 'animate');
          }
          document.body.classList.remove('overflow-hidden');
          cartCommitted = false;
          submittedVariantId = null;
          lastReservable = false;
          applyCtaState(false);
        });
        window.location.href = cartUrl;
        return;
      }
    });
    if (typeof cartUnsub === 'function') signal.addEventListener('abort', cartUnsub, { once: true });

    /* ★ ds04 #99（DECISIONS 2026-10-08（9））: 2 名以上で送って 422 が返ったときの照合（Dawn 経路だけ）。
     * Dawn の 422 は cartError だけで、上の cartUnsub（二重予約ガード）は立たない。数量が在庫を超えると「入るだけ入れて 422」
     * （公式 https://shopify.dev/docs/api/ajax/reference/cart の Error responses）。売り切れなら何も入らない。
     * どちらかを応答の後に /cart.js を 1 回読んで確かめる（Dawn の POST の応答の後なので順序は保証される）:
     *   送った variant が 1 以上 → 一部が入った＝終端ラッチ（cartCommitted）＋「人数の一部だけ〜」（再送させない）
     *   0 → 何も入っていない＝Dawn の理由文のまま CTA を戻す（従来の 1 名の 422 と同じく押し直せる）
     *   読めない → 入ったか分からない＝ラッチ（fail-closed）
     * 講座・WS タグの商品は層 (c) で送信前のカートにこの variant が無い。タグの無いイベントで送信前から同じ variant が
     * カートにあると、入っていなくてもラッチする（閉じる側・再読み込みで戻る）。
     * 1 名の 422 は対象外（1 個は入るか入らないか）。ラッチは bfcache の pageshow でも解かない（閉じる側）。 */
    const qtyPartialLabel = form.dataset.qtyPartialLabel || '';
    /* #99（Codex 実 diff P2）: 一部だけ入って PDP に留まるとき、ヘッダーのカート（ドロワー）は更新されず空の表示のまま
       （/cart へのリンクのあるフッターも隠れる）。「カートの内容をご確認ください」の横に /cart へのリンクを 1 つ添える。
       追加購入の経路の部分追加（下の (a)）も同じ。 */
    const cartLinkLabel = form.dataset.cartLinkLabel || '';
    const showCartLink = () => {
      const wrap = form.querySelector('.product-form__error-message-wrapper');
      const url = window.routes && window.routes.cart_url;
      if (!wrap || !cartLinkLabel || typeof url !== 'string' || url === '') return;
      if (wrap.querySelector('[data-tol-partial-cart-link]')) return;
      const a = document.createElement('a');
      a.href = url;
      a.className = 'link tol-kouza-partial-cart-link';
      a.textContent = cartLinkLabel;
      a.setAttribute('data-tol-partial-cart-link', '');
      wrap.appendChild(a);
    };
    const errUnsub = subscribe(PUB_SUB_EVENTS.cartError, (payload) => {
      if (!payload || payload.source !== 'product-form' || payload.tolAddon) return;
      const expected = submittedVariantId;
      if (!expected || payload.productVariantId == null || String(payload.productVariantId) !== String(expected)) return;
      if (submittedQty < 2) return;
      reconciling = true;
      applyCtaState(false);
      const desc = typeof payload.errors === 'string' ? payload.errors : '';
      (async () => {
        let inCart = null;
        try {
          const r = await fetch(`${window.routes.cart_url}.js`, { headers: { Accept: 'application/json' } });
          if (!r.ok) throw new Error(`cart.js ${r.status}`);
          const c = await r.json();
          inCart = (c.items || []).reduce(
            (n, it) => n + (String(it.variant_id) === String(expected) ? parseInt(it.quantity, 10) || 0 : 0),
            0
          );
        } catch (e) {
          inCart = null;
        }
        reconciling = false;
        if (inCart === 0) {
          /* 何も入っていない＝従来の 1 名の 422 と同じく押せる状態に戻す（Dawn の finally も aria-disabled を外す）。
             照合中に日程を確定し直していれば lastReservable はその結果（acceptConfirmed は更新だけしてロックで閉じていた）。 */
          applyCtaState(lastReservable && idInput.value !== '');
          return;
        }
        cartCommitted = true;
        lastReservable = false;
        applyCtaState(false);
        if (typeof form.handleErrorMessage === 'function' && qtyPartialLabel) {
          form.handleErrorMessage(desc ? desc + ' ' + qtyPartialLabel : qtyPartialLabel);
          showCartLink();
        }
      })();
    });
    if (typeof errUnsub === 'function') signal.addEventListener('abort', errUnsub, { once: true });

    /* ---- R15 ①（LB 7-33 E-5・DECISIONS 2026-09-08）: 追加購入オプションの submit 割り込み ----
     * LIVE aw-product-kouza.js:1361-1440 は ATC click を横取りし /cart/add.js に JSON items[]（講座＋addon）を 1 本 POST →
     * location.href='/cart'。新テーマは <product-form data-tol-kouza-form>（form の祖先）への **capture** submit で
     * Dawn product-form.js:10（form・非 capture）より先に走らせる。チェック 0 件／submit が disabled・aria-disabled／
     * カートガード blocked／[name=id] 空 なら何もせず Dawn へ委ねる（Dawn :22 と同じゲート）。
     * ★ ボタン状態は applyCtaState 経由でだけ書く（唯一の書き込み口不変）。送信中は applyCtaState(false, 追加中...)＝
     *   native disabled で連打・Enter の再 submit を構造的に止める。視覚は Dawn 既存の .loading / .loading__spinner。
     * ★ 成功時は Dawn と同じ payload で cartUpdate を publish → 上の cartUnsub（Q23・二重予約ガード）が同期 disabled →
     *   drawer 抑止 → bfcache 巻き戻し → /cart へ遷移＝LIVE の '/cart' 遷移を既存の 1 系統で得る（sections は付けない・
     *   renderContents も呼ばない）。
     * ★ 失敗時は fail-closed 側（Codex BLOCKER 是正）: 送信前後で送信対象 **全 variant** の数量を /cart.js で比較し、
     *   1 行でも増えている or 照合できない（/cart.js 不達・parse 失敗）→ CTA を復帰させず cartUpdate を publish して
     *   遷移（カートが変わった可能性がある以上 PDP に留めない。層 (c) の PDP 判定はサーバーレンダで stale）。
     *   全 variant 不変と確認できたときだけ handleErrorMessage（Dawn 既存 wrapper）で理由文を出して復帰。
     *   公式 Cart API は数量超過で「上限まで追加したうえで 422」を返す＝「422 = 何も入っていない」を前提にしない。
     *   複数 items の原子性は公式未記載＝この分岐は原子性に依存しない。 */
    const addonsRoot = scope.querySelector('[data-tol-kouza-addons]');
    if (addonsRoot && typeof publish === 'function' && window.routes) {
      let addonBusy = false;
      const addingLabel = form.dataset.addingLabel || '';
      const addonErrorLabel = form.dataset.addonErrorLabel || '';
      const partialLabel = form.dataset.addonPartialLabel || '';
      const spinner = submit.querySelector('.loading__spinner');
      const setLoading = (on) => {
        submit.classList.toggle('loading', on);
        if (spinner) spinner.classList.toggle('hidden', !on);
      };
      /* ★ R15（critical MAJOR-2）: 追加購入からの遷移後に bfcache で戻ると .loading と addonBusy が
       * snapshot に残る（finishByRedirect は遷移するので戻さない）。direct 型には復帰操作が無く
       * スピナーが回り続けるため、復元時に解く。CTA の状態は上の pageshow ハンドラが持つ。 */
      window.addEventListener('pageshow', (e) => {
        if (!e.persisted) return;
        setLoading(false);
        addonBusy = false;
      }, { signal });

      const cartSnapshot = async () => {
        const r = await fetch(`${window.routes.cart_url}.js`, { headers: { Accept: 'application/json' } });
        if (!r.ok) throw new Error(`cart.js ${r.status}`);
        const c = await r.json();
        const m = {};
        (c.items || []).forEach((it) => {
          const k = String(it.variant_id);
          m[k] = (m[k] || 0) + (parseInt(it.quantity, 10) || 0);
        });
        return m;
      };
      /* カート内容が変わった（可能性がある）ときの終わり方＝W5 cartUnsub に委ねて /cart へ */
      const finishByRedirect = (kouzaId, cartData) => {
        lastReservable = false;
        applyCtaState(false);
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'product-form', productVariantId: String(kouzaId), cartData });
      };
      form.addEventListener('submit', (evt) => {
        if (addonBusy) {
          evt.preventDefault();
          evt.stopImmediatePropagation();
          return; // 送信中の再 submit は飲み込む（Dawn へ落とすと講座単品の POST が走り部分追加になる）
        }
        const checked = Array.from(addonsRoot.querySelectorAll('[data-tol-kouza-addon]:checked')).filter((cb) => !cb.disabled);
        if (!checked.length) return; // Dawn 経路（通常の予約）
        if (submit.disabled || submit.getAttribute('aria-disabled') === 'true') return; // Dawn :22 も return
        if (pdp.dataset.tolCartGuardBlocked === 'true') return; // 構造的に owner 非装着だが明示ガード
        if (idInput.value === '') return;
        evt.preventDefault();
        evt.stopImmediatePropagation();

        const kouzaId = idInput.value;
        const qtyEl = scope.querySelector('[data-kouza-qty-root] input[name="quantity"]');
        const qty = Math.max(1, parseInt(qtyEl && qtyEl.value, 10) || 1);
        const items = [{ id: Number(kouzaId), quantity: qty }];
        checked.forEach((cb) => {
          const id = Number(cb.dataset.variantId);
          if (Number.isInteger(id) && id > 0) items.push({ id, quantity: 1 });
        });

        addonBusy = true;
        applyCtaState(false, addingLabel);
        setLoading(true);
        if (typeof form.handleErrorMessage === 'function') form.handleErrorMessage();

        (async () => {
          let before = null;
          try { before = await cartSnapshot(); } catch (e) { before = null; }
          let ok = false;
          let response = null;
          let desc = '';
          try {
            const r = await fetch(`${window.routes.cart_add_url}.js`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
              body: JSON.stringify({ items }),
            });
            ok = r.ok;
            try { response = await r.json(); } catch (e) { response = null; ok = false; }
            if (!ok && response && response.description) desc = response.description;
          } catch (e) {
            ok = false;
          }
          if (ok) {
            finishByRedirect(kouzaId, response);
            return;
          }
          /* ★ 失敗の 3 分岐（Codex security lens 2026-09-08「部分追加を成功と区別できない」の是正）:
           *   (a) 照合できた かつ 1 件でも増えた = 部分追加 → **遷移せず**理由文＋「カートの内容をご確認ください」を出し
           *       CTA は復帰させない（fail-closed・再送も起きない）。遷移すると「成功」と区別が付かないため。
           *   (b) 照合できない（/cart.js 不達・parse 失敗）→ カートが変わったか判らないので /cart へ遷移（サーバーが真実）。
           *   (c) 照合できた かつ 全件不変 = 何も入っていない → 理由文＋CTA 復帰（従来どおり）。 */
          let changed = true;
          let verified = false;
          try {
            const after = await cartSnapshot();
            if (before) {
              changed = items.some((it) => (after[String(it.id)] || 0) > (before[String(it.id)] || 0));
              verified = true;
            }
          } catch (e) {
            changed = true;
            verified = false;
          }
          if (!verified) {
            finishByRedirect(kouzaId, response); // (b)
            return;
          }
          /* tolAddon = この経路が自分で出した印（#99 の 422 の照合は追加購入の経路を対象にしない＝ここで 3 分岐を済ませている） */
          publish(PUB_SUB_EVENTS.cartError, { source: 'product-form', productVariantId: kouzaId, errors: desc, message: desc, tolAddon: true });
          if (changed) {
            /* (a) 部分追加: 追加済みの行があるので CTA は閉じたまま（再クリックで二重追加させない）。
               終端ラッチも立てる＝この後に別日程を確定しても再 enable しない（カートは既に変わっており、
               サーバーレンダの層 (c) 判定は stale。やり直しはリロード＝サーバー再評価を経る）。 */
            cartCommitted = true;
            lastReservable = false;
            applyCtaState(false);
            setLoading(false);
            addonBusy = false;
            if (typeof form.handleErrorMessage === 'function') {
              form.handleErrorMessage(desc ? desc + ' ' + partialLabel : partialLabel);
              showCartLink();
            }
            return;
          }
          if (typeof form.handleErrorMessage === 'function') form.handleErrorMessage(desc || addonErrorLabel);
          setLoading(false);
          addonBusy = false;
          applyCtaState(lastReservable && idInput.value !== ''); // (c)
        })();
      }, { capture: true, signal });
    }
  };

  /* ---- G6 SP 追従カートバー + 購入シート（launch-blockers 7-13・G6 ADR） ----
   * バー（tol-pdp-bar）は commerce 非関与＝「購入シート（tol-psheet）を開く」だけ。カート投入・購入可否・
   * sale ガードはシート内の既存 DOM（Dawn product-form + T5 サーバーレンダ）がそのまま担う。
   * シートは既存購入パネルの透明ラッパーを body class（tol-psheet-open）で fixed bottom sheet 化する
   * （PLP tol-collection-filters の body class 方式・DOM 非移動）。スクロールロックも同 class の CSS で行う
   * （Dawn の overflow-hidden-tablet はメニュードロワーが共用＝close の無条件 remove がメニューのロックを
   * 解除する競合の種になるため共有しない）。
   * 価格ミラー mirrorPrice は W5 と同じ「単一導出関数＝唯一の書き込み口」。既知の stale 2 経路
   * （利用不可 variant＝variantChange 非 publish／volume pricing＝レンジ文言）は G6 ADR 記録の受容
   * ＝シートを開けば正確な状態が見える。 */
  const initStickyBar = (pdp, scope, signal) => {
    const bar = scope.querySelector('[data-tol-pdp-bar]');
    if (!bar) return; /* WS（tol-main-product-kouza はバー非搭載）・product blank では非初期化 */
    const openBtn = bar.querySelector('[data-tol-pdp-bar-open]');
    const amount = bar.querySelector('[data-tol-pdp-bar-amount]');
    const sheet = scope.querySelector('[data-tol-psheet]');
    const closeBtn = scope.querySelector('[data-tol-psheet-close]');
    const overlay = scope.querySelector('[data-tol-psheet-overlay]');
    const anchor = scope.querySelector('[data-tol-psheet-anchor]'); /* CLS 対策の外殻（欠落時はスキップ＝従来挙動） */
    if (!openBtn || !sheet) return; /* 契約欠落 → bar hidden のまま（fail-closed） */

    const OPEN_CLASS = 'tol-psheet-open'; /* PLP mfilter-open と非衝突・スクロールロック CSS も担う */
    const CLOSING_CLASS = 'tol-psheet-closing'; /* 閉じアニメ中間状態（OPEN と併存・CSS が slide-down を差す） */
    const BAR_CLASS = 'tol-pdp-bar-active'; /* 下部余白（body padding-bottom）トリガ */
    const isOpen = () => document.body.classList.contains(OPEN_CLASS);
    let closing = false; /* 閉じアニメ進行中フラグ（isOpen は closing 中も true＝ロック/退避を維持） */
    let closeSession = 0; /* 古い animationend/timeout が後続セッションを誤確定しないためのトークン */

    const open = () => {
      if (closing) {
        /* 閉じアニメ中の再オープン: closing を取り消すだけで開状態へ復帰する
         * （OPEN_CLASS・trapFocus・aria・anchor 高さは finishClose まで生きているため再構築不要）。 */
        closing = false;
        closeSession++;
        document.body.classList.remove(CLOSING_CLASS);
        return;
      }
      if (isOpen()) return;
      /* ★CLS 対策（ユーザー指摘）: シートが fixed 化すると in-flow の場所が collapse し
       * ページ全体がレイアウトシフトする。fixed 化の前に現在高さを外殻 anchor へ焼き込み、
       * 開いている間も元の場所を同じ高さで保持する。さらに offsetHeight では「透明ラッパーを
       * 透過して外と collapse していた margin」を含められない（DEV 実測で -5px 台の残差）ため、
       * fixed 化直後に後続コンテンツの実位置ズレを同期で測り anchor 高さへ補正加算する。
       * 全て同一タスク内の同期実行＝ブラウザは中間状態を描画しない＝CLS ゼロ。
       * 開中に variant 切替で中身高さが変わっても anchor は開時値で固定（背景は overlay 裏・
       * 閉時にクリアして実高さへ戻る）。 */
      /* ★スクロールバー補償（CLS 対策の一部）: overflow:hidden で classic scrollbar（Windows 等）が
       * 消えるとビューポート幅が広がり、幅依存セクションの折返し変化で縦シフトが出る（DEV 実測 +7.5px）。
       * 消える幅を body padding-right で予約して幅を不変に保つ（SP/overlay scrollbar 環境では 0＝no-op）。 */
      const sbw = window.innerWidth - document.documentElement.clientWidth;
      if (sbw > 0) document.body.style.setProperty('--tol-psheet-sbw', sbw + 'px');
      if (anchor) {
        const nextEl = anchor.nextElementSibling;
        const beforeTop = nextEl ? nextEl.getBoundingClientRect().top : null;
        /* in-flow 高さは fixed 化の前に1回だけ測って使い回す（fixed 化後の offsetHeight は
         * シートヘッダー表示+padding で別物になる＝再読みすると過大補正になる罠・DEV 実測）。 */
        const inFlowH = sheet.offsetHeight;
        anchor.style.height = inFlowH + 'px';
        document.body.classList.add(OPEN_CLASS);
        if (nextEl) {
          const drift = beforeTop - nextEl.getBoundingClientRect().top;
          if (drift) anchor.style.height = (inFlowH + drift) + 'px';
        }
      } else {
        document.body.classList.add(OPEN_CLASS);
      }
      openBtn.setAttribute('aria-expanded', 'true');
      /* dialog セマンティクスは開時のみ付与（閉時は素の in-flow 領域＝常設 role は誤り） */
      sheet.setAttribute('role', 'dialog');
      sheet.setAttribute('aria-modal', 'true');
      sheet.setAttribute('aria-labelledby', 'TolPsheetTitle-' + (pdp.dataset.section || ''));
      if (typeof trapFocus === 'function') trapFocus(sheet, closeBtn || sheet);
      else if (closeBtn) closeBtn.focus();
    };

    /* finishClose = 閉じ確定（旧 close 本体）。内部順序は「body class 除去（バー可視化）→
     * removeTrapFocus(focusTarget)」を固定する。逆順だと visibility:hidden のバー内 openBtn への
     * focus() が無言で不発（フォーカス迷子）。 */
    const finishClose = (focusTarget) => {
      closing = false;
      closeSession++;
      document.body.classList.remove(CLOSING_CLASS, OPEN_CLASS);
      if (anchor) anchor.style.height = ''; /* in-flow 復帰後に場所保持を解除（CLS 対策の対） */
      document.body.style.removeProperty('--tol-psheet-sbw'); /* スクロールバー補償の解除 */
      openBtn.setAttribute('aria-expanded', 'false');
      sheet.removeAttribute('role');
      sheet.removeAttribute('aria-modal');
      sheet.removeAttribute('aria-labelledby');
      if (typeof removeTrapFocus === 'function') removeTrapFocus(focusTarget || null);
      else if (focusTarget) focusTarget.focus();
    };

    /* close = 手動閉（×/overlay/ESC）は slide-down アニメ（CLOSING_CLASS・OPEN 併存）後に確定。
     * immediate=true は即時確定＝必須の経路: ①cartUpdate 自動クローズ（removeTrapFocus が
     * cart-drawer の trapFocus に同期で先行する要件・G6 ADR。cart_type=drawer 化後は cart-notification でなく
     * cart-drawer が開く＝drawer の trapFocus は open(setTimeout)後の transitionend 後でさらに遅い＝先行は一層強い）
     * ②unload teardown ③PC 幅跨ぎ掃除。reduced-motion はアニメを飛ばして即確定（CSS 側も animation:none）。 */
    const close = (focusTarget, immediate) => {
      if (!isOpen()) return;
      if (closing) {
        if (immediate) finishClose(focusTarget); /* アニメ中の強制確定（teardown 等） */
        return;
      }
      const reduced = typeof window.matchMedia === 'function'
        && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (immediate || reduced) {
        finishClose(focusTarget);
        return;
      }
      closing = true;
      const session = ++closeSession;
      document.body.classList.add(CLOSING_CLASS);
      const settle = () => {
        if (closing && closeSession === session) finishClose(focusTarget);
      };
      sheet.addEventListener('animationend', settle, { once: true, signal });
      setTimeout(settle, 400); /* animationend 不発（タブ非表示・アニメ中断等）のフォールバック */
    };

    openBtn.addEventListener('click', open, { signal });
    if (closeBtn) closeBtn.addEventListener('click', () => close(openBtn), { signal });
    if (overlay) overlay.addEventListener('click', () => close(openBtn), { signal });
    document.addEventListener('keydown', (e) => {
      /* #25: 店舗在庫ドロワー（<dialog>）が開いている間は Esc をシートへ届かせない（二層閉じの保険・critical m-4） */
      if (document.querySelector('dialog[data-tol-store-stock][open]')) return;
      if (e.key === 'Escape' && isOpen()) close(openBtn);
    }, { signal });
    /* PC 幅へ跨いだら状態掃除（CSS は media で既に無効だが body class / trapFocus の残留を防ぐ） */
    const mq = window.matchMedia('(min-width: 990px)');
    if (typeof mq.addEventListener === 'function') {
      mq.addEventListener('change', () => { if (mq.matches) close(null, true); }, { signal });
    }

    /* 価格ミラー（単一導出関数＝バー価格への唯一の書き込み口）。実 DOM の #price（variantChange 時点で
     * product-info.js が innerHTML 差替済み）から現行価格を抽出。失敗時は何もしない（焼き込み値のまま）。 */
    const mirrorPrice = () => {
      if (!amount) return;
      const host = scope.querySelector('#price-' + (pdp.dataset.section || ''));
      /* セール時の 4 行（ds04 #4）は tol のマークアップ＝販売価格の要素を data 属性で指す */
      const current = host && host.querySelector('[data-tol-price-current]');
      if (current) {
        const cur = current.textContent.trim();
        if (cur) amount.textContent = cur;
        return;
      }
      const root = host && host.querySelector('.price');
      if (!root) return;
      const sel = root.classList.contains('price--on-sale')
        ? '.price__sale .price-item--sale'
        : '.price__regular .price-item--regular';
      const item = root.querySelector(sel);
      const txt = item ? item.textContent.trim() : '';
      if (txt) amount.textContent = txt;
    };

    /* ds04 #41（2026-10-08）: 本文 #price の変化をバーへ写す。定期便アプリ（huckleberry）は単一バリアントでも
     * `.price .price-item--regular` を最初のプランの価格へ書き換える（variantChange は来ない）ため、バーが元の価格の
     * ままだった。Dawn の innerHTML 差し替えも拾う（variantChange の購読と二重に写すが値は同じ）。mirrorPrice は
     * #price の外（バー）にだけ書く＝ループしない。初期化時には写さない（アプリの書き換え前に走ると、Liquid が
     * 焼いた定期の価格を一回の価格で上書きする）。rAF は 1 フレーム 1 回にまとめ、teardown で取り消す。 */
    const priceHost = scope.querySelector('#price-' + (pdp.dataset.section || ''));
    if (priceHost && amount && typeof MutationObserver === 'function') {
      let frame = 0;
      const priceObserver = new MutationObserver(() => {
        if (frame) return;
        frame = requestAnimationFrame(() => {
          frame = 0;
          if (!signal.aborted) mirrorPrice();
        });
      });
      priceObserver.observe(priceHost, { childList: true, subtree: true, characterData: true });
      signal.addEventListener('abort', () => {
        priceObserver.disconnect();
        if (frame) cancelAnimationFrame(frame);
        frame = 0;
      }, { once: true });
    }

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      /* variantChange（product-info.js が #price innerHTML 差替後に publish）→ rAF 後追いでミラー。
       * シート開中は trapFocus を張り直す（updateOptionValues が variant-selects を丸ごと差し替えるため
       * トラップの first/last 端点が detached になり Tab がダイアログ外へ漏れる＝trapFocus は呼び出し時に
       * 1 回だけ端点捕捉する global.js の仕様）。 */
      const unsubV = subscribe(PUB_SUB_EVENTS.variantChange, (payload) => {
        if (!payload?.data?.sectionId || payload.data.sectionId !== pdp.dataset.section) return;
        requestAnimationFrame(() => {
          mirrorPrice();
          /* ★フォーカス保全型 re-trap（実 diff 二系統レビュー両系統一致の是正）: trapFocus は端点再捕捉と
           * 無条件 focus() が不可分（global.js:124）。Dawn は variant 切替後に選んだオプションへフォーカスを
           * 復元する（product-info.js:128-129・microtask）ため、rAF で closeBtn を渡すと毎回それを × へ
           * 強奪する退行になる。現フォーカスがシート内で生きていればそれを elementToFocus に渡して維持し、
           * detached/シート外（差し替えで消えた等）のときだけ closeBtn へ fallback する。 */
          if (isOpen() && typeof trapFocus === 'function') {
            const a = document.activeElement;
            const keep = a && a !== document.body && a.isConnected && sheet.contains(a);
            trapFocus(sheet, keep ? a : (closeBtn || sheet));
          }
        });
      });
      if (typeof unsubV === 'function') signal.addEventListener('abort', unsubV, { once: true });

      /* カート追加成功 → シート自動クローズ（cart-type=drawer 化後は cart-drawer を開いて見せる。旧 cart-notification）。
       * ★同期実行が必須（rAF 禁止）: pubsub.publish は購読 callback をその場で同期実行し、その直後に
       *   product-form.js が同期的に renderContents（cart-drawer 描画）→ cart-drawer 側の trapFocus は
       *   open(cart-drawer.js:86-89 setTimeout)後の transitionend 後＝旧 cart-notification よりさらに遅い（二段遅延）。
       *   同期 close なら removeTrapFocus が cart-drawer トラップに決定的に先行する（drawer は二段遅延で一層後＝先行が強い・
       *   rAF は逆転レースの種で禁止）。cart_type=notification に戻したときも cart-notification の transitionend 後より先行。
       * ★try/catch 必須: pubsub は購読 callback の例外を隔離しない。ここで throw すると同じ .then 内の
       *   renderContents が実行されず「カートに入ったのに cart-drawer が開かない」silent break（S19b 同型連鎖）。
       * エラー経路（在庫不足等）は cartError publish＝cartUpdate 非発火＝閉じない（エラー枠はシート内）。
       * @app 独自購入 UI・dynamic checkout は cartUpdate 非発火＝自動 close 対象外（G6 ADR 受容）。
       * フォーカスは無指定で閉じる＝close 時に product-form.js が保存した submit 時 activeElement
       * （in-flow に戻った本体ボタン）へ復帰する既存機構に委ねる。 */
      const unsubC = subscribe(PUB_SUB_EVENTS.cartUpdate, (payload) => {
        try {
          if (!payload || payload.source !== 'product-form') return;
          if (payload.productVariantId == null) return;
          const idInput = scope.querySelector('.product-variant-id');
          const match = idInput && String(payload.productVariantId) === String(idInput.value);
          /* ★ close 判定（drawer 化で顕在化した交差の是正・Copilot MEDIUM）:
           * 通常は自 PDP variant 一致で close。ただし /cart/add 保留中に variant を切り替えると Dawn（product-info.js）が
           * cartUpdate の前に .product-variant-id を新 variant へ更新するため、payload の旧 variant（POST 時に固定・
           * product-form.js:73 formData.get('id')）≠ 現 idInput の新 variant で不一致になり close がスキップされる。
           * すると購入シート（tol-psheet・z-index 10070）が開いたまま cart-drawer（z-index 1000）がその下に開き
           * drawer がシートに隠れてブロックされて見える（cart-notification 前提では受容だったが drawer 化で UX 事故）。
           * ★ シート開中（isOpen()）は「このシートの購入パネルからの追加」と見なし variant 不一致でも close する。
           *   本 PDP ページ内に他の <product-form> は存在しない（おすすめ/最近見た商品は tol-product-card＝quick-add
           *   非搭載・S15 ADR で Dawn card-product 非流用／PDP の product-form は本体 tol-main-product の 1 つのみ・
           *   実コードで確認）ため、シート開中の source==='product-form' な cartUpdate は必ず本体由来＝誤クローズしない。
           * ★ 閉時（!isOpen()）は従来どおり variant 厳密一致のみ（シートが閉じているのに他要因の cartUpdate で
           *   誤って close 経路に入らないよう厳密側に倒す・close は isOpen() ガードで no-op だが判定を明示的に絞る）。 */
          if (!match && !isOpen()) return;
          close(null, true); /* 即時＝removeTrapFocus が cart-drawer トラップに同期で先行（アニメ化しない） */
        } catch (e) {
          /* 握りつぶし＝renderContents（cart-drawer 描画）の連鎖を守る。close 失敗は手動 × / overlay で回復可能。 */
        }
      });
      if (typeof unsubC === 'function') signal.addEventListener('abort', unsubC, { once: true });
    }

    /* 初期化成功 → バー reveal + 下部余白（S19a fail-closed の解除） */
    bar.hidden = false;
    document.body.classList.add(BAR_CLASS);
    pdp._tolPsheet = {
      close: () => close(null, true), /* teardown は即時確定（アニメ待ちで class 残留させない） */
      deactivate: () => document.body.classList.remove(BAR_CLASS),
    };
  };

  /* ---- #21 受注生産品の案内の variant 追従（DECISIONS 2026-10-02（9）） ----
   * 判定と表示はサーバー（snippets/tol-pdp-bto-notice.liquid ← tol-variant-bto）。ここは写すだけで判定を持たない。
   *   - variantChange（product-info.js:204 が描き直した section の HTML を data.html で渡す）→ 枠の中身を写す。
   *   - 利用不可の組み合わせ（variant が null）は product-info.js:173-176 が setUnavailable() して return し
   *     variantChange を publish しない。直前の updateVariantInputs（:214-221）が自 PDP の form の id 入力を空にして
   *     change を出すので、それを受けて枠を空にする（前の variant の案内を残さない・G6 の受容はバー価格だけの例外）。
   * 枠が無い PDP（講座 tol-main-product-kouza）は何もしない。#25 の文言の枠もここで同じ写し方を使う想定（ADR）。 */
  const initBtoNotice = (pdp, signal) => {
    const sectionId = pdp.dataset.section;
    const slotId = `TolBto-${sectionId}`;
    if (!sectionId || !document.getElementById(slotId)) return;
    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;

    const unsub = subscribe(PUB_SUB_EVENTS.variantChange, (payload) => {
      try {
        /* 自セクション以外の variantChange は無視（複数 PDP / quick-add 共存時の取り違え防止）。 */
        if (payload?.data?.sectionId && payload.data.sectionId !== sectionId) return;
        const source = payload?.data?.html?.getElementById?.(slotId);
        const destination = document.getElementById(slotId);
        /* 中身が同じなら書かない（受注生産品どうしの切り替えで live region が毎回読み上げ直さないように）。 */
        if (source && destination && destination.innerHTML !== source.innerHTML) {
          destination.innerHTML = source.innerHTML;
        }
      } catch (e) {
        /* pubsub は購読 callback の例外を隔離しない＝後続の購読者（追従バーほか）を止めない。 */
        console.error('[tol-pdp-form] bto notice の差し替えで例外', e);
      }
    });
    if (typeof unsub === 'function') signal.addEventListener('abort', unsub, { once: true });

    pdp.addEventListener(
      'change',
      (e) => {
        const input = e.target;
        if (!(input instanceof HTMLInputElement) || input.name !== 'id') return;
        if (!input.closest(`#product-form-${sectionId}`)) return;
        if (input.value !== '') return;
        const destination = document.getElementById(slotId);
        if (destination) destination.innerHTML = '';
      },
      { signal }
    );
  };

  /* ---- #25 店舗在庫ドロワー（DECISIONS 2026-10-05・LIVE の liquid ブロック liquid_ydCznA の移植） ----
   * 判定（受注生産 / standby）と文言はサーバー（snippets/tol-pdp-store-stock.liquid ← tol-variant-bto / tol-variant-standby）。
   * ここは ①<dialog> の開閉（ギフト案内 tol-cart-page.js と同型: showModal・overflow-hidden は自分が付けた分だけ外す・
   * backdrop は座標判定・close イベントで opener へ focus）②店舗一覧の初回取得（?sections=tol-store-stock-list・1 回だけ）
   * ③Omni Hub のアプリプロキシへの在庫照会（LIVE と同じ query・リクエストごとに abort・15 秒タイムアウト）
   * ④ドロワーのセレクト → 本体の variant 同期（option value id で Dawn の variant-selects を動かす＝多軸でも動く）
   * ⑤本体の variant 切替（variantChange）→ ボタンの disabled と案内の枠を描き直した HTML から写す・開いていれば照会し直す。
   * ★ data-tol-endpoint が空なら fetch しない（検証で実通信を止める構造的ガード・critical M-3）。
   * ★ 競合: seq（照会の世代）・openSession（open の世代）・pendingVariantId（本体の同期待ち）で古い継続を捨てる（Codex P2・critical M-2）。 */
  const initStoreStock = (pdp, scope, signal) => {
    const sectionId = pdp.dataset.section;
    const btn = scope.querySelector('[data-tol-store-stock-open]');
    const dialog = scope.querySelector('[data-tol-store-stock]');
    if (!btn || !dialog) return; /* kouza PDP・product blank では非初期化 */
    const select = dialog.querySelector('[data-tol-store-stock-select]');
    const filter = dialog.querySelector('[data-tol-store-stock-filter]');
    const btoBox = dialog.querySelector('[data-tol-store-stock-bto]');
    const list = dialog.querySelector('[data-tol-store-stock-list]');
    const status = dialog.querySelector('[data-tol-store-stock-status]');
    const noteId = `TolStoreStockNote-${sectionId}`;
    /* 商品タグ「予約」: サーバーが案内文だけを描く＝照会しない（店舗在庫を出さない） */
    const preorder = dialog.hasAttribute('data-tol-preorder');
    if (!select || !list || !status) return;

    const canModal =
      typeof HTMLDialogElement === 'function' && typeof HTMLDialogElement.prototype.showModal === 'function';
    const locatorUrl = btn.getAttribute('data-tol-store-locator-url') || '';
    if (!canModal) {
      /* <dialog> 非対応: 店舗検索ページへのリンクとして働かせる。URL が無ければ死んだボタンを出さない。 */
      if (!locatorUrl) {
        btn.hidden = true;
        return;
      }
      btn.addEventListener('click', () => window.location.assign(locatorUrl), { signal });
      return;
    }

    const listUrl = dialog.getAttribute('data-tol-list-url') || '';
    const hideIds = (dialog.getAttribute('data-tol-hide-store-ids') || '')
      .split(',')
      .map((s) => Number(s.trim()))
      .filter((n) => !Number.isNaN(n));
    const I18N = {
      loading: dialog.dataset.tolI18nLoading || '',
      error: dialog.dataset.tolI18nError || '',
      none: dialog.dataset.tolI18nNone || '',
    };
    const QUERY =
      'query storeStock($input: StoreStockInput!) { stock(input: $input) { id stockItems { __typename id storeId storeName stockAmount } } }';
    const TIMEOUT_MS = 15000;
    const SYNC_TIMEOUT_MS = 8000;

    let locked = false;
    let listPromise = null;
    let inflight = null;
    let seq = 0;
    let openSession = 0;
    let pendingVariantId = null;
    let syncTimer = 0;
    let sortedOnce = false;

    const currentVariantId = () => scope.querySelector('.product-variant-id')?.value || '';
    const optionFor = (vid) => Array.from(select.options).find((o) => o.value === String(vid)) || null;
    const setStatus = (text) => {
      status.textContent = text || '';
    };
    const lock = () => {
      /* 既に他（カートドロワー等）が付けていれば触らない（tol-cart-page.js と同型）。classic scrollbar の幅は予約（CLS 対策） */
      if (document.body.classList.contains('overflow-hidden')) return;
      const sbw = window.innerWidth - document.documentElement.clientWidth;
      if (sbw > 0) document.body.style.paddingRight = `${sbw}px`;
      document.body.classList.add('overflow-hidden');
      locked = true;
    };
    const unlock = () => {
      if (!locked) return;
      document.body.classList.remove('overflow-hidden');
      document.body.style.removeProperty('padding-right');
      locked = false;
    };
    const invalidate = () => {
      seq++;
      if (inflight) {
        inflight.abort();
        inflight = null;
      }
      pendingVariantId = null;
      if (syncTimer) {
        clearTimeout(syncTimer);
        syncTimer = 0;
      }
    };

    /* ---- 店舗名の突合・並び（LIVE 逐語） ---- */
    const clean = (s) => (s ? s.replace(/生活の木/g, '').replace(/[\s　\-\(\)（）]/g, '').toLowerCase().trim() : '');
    const PREF_ORDER = [
      '北海道',
      '青森', '岩手', '宮城', '秋田', '山形', '福島',
      '東京都23区外', '茨城', '栃木', '群馬', '埼玉', '千葉', '神奈川',
      '新潟', '富山', '石川', '福井', '山梨', '長野', '岐阜', '静岡', '愛知', '三重',
      '滋賀', '京都', '大阪', '兵庫', '奈良', '和歌山',
      '鳥取', '島根', '岡山', '広島', '山口',
      '徳島', '香川', '愛媛', '高知',
      '福岡', '佐賀', '長崎', '熊本', '大分', '宮崎', '鹿児島', '沖縄',
    ];
    const prefIndex = (name) => {
      if (!name) return 999;
      const n = name.replace(/[都道府県]$/, '').trim();
      let idx = PREF_ORDER.findIndex((p) => p.replace(/[都道府県]$/, '').trim() === n);
      if (idx !== -1) return idx;
      idx = PREF_ORDER.findIndex((p) => n.includes(p.replace(/[都道府県]$/, '').trim()));
      return idx !== -1 ? idx : 999;
    };
    const sortStores = () => {
      list.querySelectorAll('.tol-store-stock__region-body').forEach((container) => {
        const rows = Array.from(container.querySelectorAll('[data-tol-store-stock-row]'));
        rows.sort((a, b) => {
          const ia = prefIndex(a.dataset.pref);
          const ib = prefIndex(b.dataset.pref);
          if (ia !== ib) return ia - ib;
          const za = a.dataset.zipcode || '9999999';
          const zb = b.dataset.zipcode || '9999999';
          if (za !== '9999999' || zb !== '9999999') return za.localeCompare(zb, undefined, { numeric: true });
          return (a.dataset.address || '').localeCompare(b.dataset.address || '', 'ja');
        });
        rows.forEach((row) => container.appendChild(row));
      });
    };
    const statusKind = (amount, isStandby) => {
      const high = isStandby ? 3 : 10;
      const low = isStandby ? 1 : 4;
      if (amount >= high) return 'ok';
      if (amount >= low) return 'few';
      return 'out';
    };
    const tpl = (kind) => dialog.querySelector(`[data-tol-store-stock-tpl="${kind}"]`);

    /* 可視行の集計: 空の地方群を隠し、取得済みで可視行 0 なら「該当する店舗がありません」（フィルタ変更にも追従・Codex P2-7） */
    const applyVisibility = () => {
      const filtering = dialog.dataset.tolFilter === 'instock';
      let visible = 0;
      list.querySelectorAll('[data-tol-store-stock-region]').forEach((region) => {
        const shown = Array.from(region.querySelectorAll('[data-tol-store-stock-row]')).filter(
          (r) => !r.hidden && (!filtering || r.dataset.stockStatus === 'ok' || r.dataset.stockStatus === 'few')
        );
        region.hidden = shown.length === 0;
        visible += shown.length;
      });
      if (!list.hidden) setStatus(visible === 0 ? I18N.none : '');
    };

    /* ---- 店舗一覧（初回だけ・成功時だけキャッシュ） ---- */
    const ensureList = () => {
      if (listPromise) return listPromise;
      /* ★ Accept: application/json を付けない（/products/<handle> に付けると Shopify は商品の JSON を返し sections の JSON にならない・DEV 実測） */
      listPromise = fetch(listUrl, { signal })
        .then((res) => {
          if (!res.ok) throw new Error(`store list ${res.status}`);
          return res.json();
        })
        .then((json) => {
          const html = json && json['tol-store-stock-list'];
          if (typeof html !== 'string') throw new Error('store list shape');
          const root = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-tol-store-stock-list-root]');
          if (!root) throw new Error('store list root');
          list.replaceChildren(...Array.from(root.children));
          if (!sortedOnce) {
            sortStores();
            sortedOnce = true;
          }
        })
        .catch((err) => {
          listPromise = null;
          throw err;
        });
      return listPromise;
    };

    /* ---- 在庫照会（選択中の option 1 件・リクエストごとに abort） ---- */
    const runFor = (opt) => {
      if (!opt || preorder) return;
      invalidate();
      const my = seq;
      if (opt.dataset.bto === 'true') {
        if (btoBox) btoBox.hidden = false;
        list.hidden = true;
        setStatus('');
        return; /* 受注生産は照会しない（LIVE と同じ） */
      }
      if (btoBox) btoBox.hidden = true;
      list.hidden = true;
      const endpoint = dialog.getAttribute('data-tol-endpoint') || '';
      if (!endpoint) {
        setStatus(I18N.error);
        return; /* 構造的ガード: endpoint が無ければ通信しない */
      }
      setStatus(I18N.loading);
      /* 店舗一覧が無いまま照会すると「該当する店舗がありません」に化ける（Codex P2-3）＝一覧の取得を必ず先に通す（成功時はキャッシュ済み） */
      ensureList()
        .then(() => {
          if (my !== seq) return;
          query(opt, my, endpoint);
        })
        .catch(() => {
          if (my !== seq) return;
          list.hidden = true;
          setStatus(I18N.error);
        });
    };
    const query = (opt, my, endpoint) => {
      const ctrl = new AbortController();
      inflight = ctrl;
      let timedOut = false;
      const onAbort = () => ctrl.abort();
      signal.addEventListener('abort', onAbort, { once: true });
      const timer = setTimeout(() => {
        timedOut = true;
        ctrl.abort();
      }, TIMEOUT_MS);
      fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ query: QUERY, variables: { input: { shopifyProductVariantId: String(opt.value) } } }),
        signal: ctrl.signal,
      })
        .then((res) => {
          if (!res.ok) throw new Error(`stock ${res.status}`);
          return res.json();
        })
        .then((json) => {
          if (my !== seq) return;
          const items = json?.data?.stock?.stockItems;
          if ((Array.isArray(json?.errors) && json.errors.length) || !json?.data?.stock || !Array.isArray(items)) {
            throw new Error('stock graphql');
          }
          const isStandby = opt.dataset.standby === 'true';
          const map = {};
          items.forEach((item) => {
            if (hideIds.includes(Number(item.storeId))) return;
            map[clean(item.storeName)] = statusKind(Number(item.stockAmount), isStandby);
          });
          const keys = Object.keys(map);
          list.querySelectorAll('[data-tol-store-stock-row]').forEach((row) => {
            const target = clean(row.dataset.storeName);
            const found = target ? keys.find((k) => k === target || k.includes(target) || target.includes(k)) : undefined;
            const receiver = row.querySelector('[data-tol-store-stock-receiver]');
            if (found) {
              const kind = map[found];
              row.dataset.stockStatus = kind;
              const t = tpl(kind);
              if (receiver && t) receiver.replaceChildren(t.content.cloneNode(true));
              row.hidden = false;
            } else {
              row.dataset.stockStatus = 'none';
              row.hidden = true;
            }
          });
          list.hidden = false;
          applyVisibility();
        })
        .catch((err) => {
          if (my !== seq) return;
          if (err && err.name === 'AbortError' && !timedOut) return; /* close・選択変更・teardown 由来の中断は無表示 */
          list.hidden = true;
          setStatus(I18N.error);
        })
        .finally(() => {
          clearTimeout(timer);
          signal.removeEventListener('abort', onAbort);
          if (inflight === ctrl) inflight = null;
        });
    };

    /* ---- ドロワーのセレクト → 本体の variant 同期（option value id で Dawn の variant-selects を動かす） ---- */
    /* force = 本体の確定 variant（.product-variant-id）が目的の variant と違うときは、option の見かけが既に一致していても
     * change を出す（前回の同期失敗で控えだけが変わっている・Codex P2-2）。 */
    const syncPage = (idsAttr, force) => {
      const vs = scope.querySelector('variant-selects');
      if (!vs || !idsAttr || vs.closest('[inert]')) return false; /* sale 期間外（inert）は本体を動かさない */
      const ids = idsAttr.split(',').map((s) => s.trim()).filter(Boolean);
      if (!ids.length) return false;
      let last = null;
      let changed = 0;
      const dropdowns = []; /* { sel, el } ドロップダウン型の軸（属性の後始末に使う） */
      for (const id of ids) {
        const el = vs.querySelector(`[data-option-value-id="${CSS.escape(id)}"]`);
        if (!el) return false;
        if (el instanceof HTMLOptionElement) {
          const sel = el.closest('select');
          if (!sel) return false;
          /* ★ プロパティだけで選ぶ（属性はここで動かさない）。Dawn の updateSelectionMetadata（global.js）は change の中で
           * 「selected 属性を持つ古い option から外し、selectedOptions[0] に付け替える」。先にこちらが属性を新しい option へ
           * 移すと、Dawn は新しい option の属性を外し、Chrome はその option の選択を解除して先頭へ戻す（DEV 実測）。
           * 属性の付け替えは change を受ける select については Dawn に任せ、それ以外の軸だけ下で揃える。 */
          if (!el.selected) {
            el.selected = true;
            changed++;
          }
          dropdowns.push({ sel, el });
          last = sel;
        } else if (el instanceof HTMLInputElement) {
          if (!el.checked) {
            el.checked = true;
            changed++;
          }
          last = el;
        } else {
          return false;
        }
      }
      if (!last || (!changed && !force)) return false;
      /* 複数軸のドロップダウン: change を受けない select の selected 属性は Dawn が動かさないので、ここで揃える
       * （新しい option に先に付けてから古い属性を外す＝選択済みの option を残したまま外すので reset が走らない）。 */
      dropdowns.forEach(({ sel, el }) => {
        if (sel === last) return;
        el.setAttribute('selected', 'selected');
        Array.from(sel.options).forEach((o) => {
          if (o !== el) o.removeAttribute('selected');
        });
      });
      last.dispatchEvent(new Event('change', { bubbles: true }));
      return true;
    };

    const onSelect = () => {
      const opt = select.selectedOptions[0];
      if (!opt) return;
      invalidate();
      list.hidden = true;
      if (btoBox) btoBox.hidden = true;
      if (opt.dataset.bto === 'true') {
        runFor(opt);
        return;
      }
      setStatus(I18N.loading);
      /* 確定 variant と同じでも本体の控えを必ず見る（A→B→A と素早く戻したとき、B の取得中に A へ戻す change を出して
       * Dawn 側の B の取得を取り消す・Codex P2-1）。控えも確定も一致していれば本体は動かさず直接照会。 */
      const force = String(opt.value) !== currentVariantId();
      if (!syncPage(opt.dataset.optionValueIds || '', force)) {
        runFor(opt); /* 本体を動かせない（inert 等）ときはドロワー内だけで照会 */
        return;
      }
      /* 本体の描き直し → variantChange で照会する（POST 経路は 1 本）。来なければ失敗として戻す。 */
      pendingVariantId = String(opt.value);
      const my = seq;
      syncTimer = setTimeout(() => {
        syncTimer = 0;
        if (my !== seq || pendingVariantId !== String(opt.value)) return;
        pendingVariantId = null;
        setStatus(I18N.error);
        const cur = optionFor(currentVariantId());
        if (cur) select.value = cur.value;
      }, SYNC_TIMEOUT_MS);
    };

    const open = () => {
      if (dialog.open) return;
      const s = ++openSession;
      const cur = optionFor(currentVariantId());
      if (cur) select.value = cur.value;
      dialog.showModal();
      lock();
      list.hidden = true;
      if (btoBox) btoBox.hidden = true;
      if (preorder) return; /* 予約商品は一覧も在庫も取らない */
      setStatus(I18N.loading);
      ensureList()
        .then(() => {
          if (s !== openSession || !dialog.open) return; /* 一覧の取得中に閉じた・開き直した */
          runFor(select.selectedOptions[0]);
        })
        .catch(() => {
          if (s !== openSession || !dialog.open) return;
          list.hidden = true;
          setStatus(I18N.error);
        });
    };

    pdp.addEventListener(
      'click',
      (e) => {
        const t = e.target;
        if (!(t instanceof Element)) return;
        if (t.closest('[data-tol-store-stock-open]')) {
          e.preventDefault();
          open();
          return;
        }
        if (t !== dialog && !dialog.contains(t)) return;
        if (t.closest('[data-tol-store-stock-close]')) {
          dialog.close();
          return;
        }
        if (t === dialog) {
          /* backdrop の判定は座標で（dialog がスクロールバーを持つ環境では target === dialog になる・tol-cart-page.js と同型） */
          const r = dialog.getBoundingClientRect();
          const inside = e.clientX >= r.left && e.clientX <= r.right && e.clientY >= r.top && e.clientY <= r.bottom;
          if (!inside) dialog.close();
        }
      },
      { signal }
    );
    pdp.addEventListener(
      'change',
      (e) => {
        const t = e.target;
        if (t === select) {
          onSelect();
          return;
        }
        if (filter && t === filter) {
          dialog.dataset.tolFilter = filter.checked ? 'instock' : '';
          applyVisibility();
          return;
        }
        /* 利用不可の組み合わせ（Dawn が id 入力を空にして change）: ボタンを無効にし案内の枠を空に */
        if (t instanceof HTMLInputElement && t.name === 'id' && t.value === '' && t.closest(`#product-form-${sectionId}`)) {
          btn.disabled = true;
          const note = document.getElementById(noteId);
          if (note) note.innerHTML = '';
          invalidate();
        }
      },
      { signal }
    );
    dialog.addEventListener(
      'close',
      () => {
        openSession++;
        unlock();
        invalidate();
        if (btn.isConnected && !btn.disabled) btn.focus();
      },
      { signal }
    );
    /* Esc はネイティブの close に任せ、document の購入シートのハンドラへは届かせない */
    dialog.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') e.stopPropagation();
    }, { signal });

    /* 本体の variant 切替に追従（自セクションだけ・initBtoNotice と同じ写し方） */
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      const unsub = subscribe(PUB_SUB_EVENTS.variantChange, (payload) => {
        try {
          if (payload?.data?.sectionId && payload.data.sectionId !== sectionId) return;
          const html = payload?.data?.html;
          const src = html?.querySelector?.('[data-tol-store-stock-open]');
          if (src) btn.disabled = src.hasAttribute('disabled');
          const note = document.getElementById(noteId);
          const srcNote = html?.getElementById?.(noteId);
          if (note && srcNote && note.innerHTML !== srcNote.innerHTML) note.innerHTML = srcNote.innerHTML;
          const vid = String(payload?.data?.variant?.id || currentVariantId());
          const opt = optionFor(vid);
          if (opt) select.value = opt.value;
          if (!dialog.open) return;
          if (pendingVariantId && pendingVariantId !== vid) return; /* 別の切替＝自分の同期待ちではない */
          if (syncTimer) {
            clearTimeout(syncTimer);
            syncTimer = 0;
          }
          pendingVariantId = null;
          runFor(opt || select.selectedOptions[0]);
        } catch (err) {
          /* pubsub は購読 callback の例外を隔離しない＝後続の購読者を止めない */
          console.error('[tol-pdp-form] store stock の variant 追従で例外', err);
        }
      });
      if (typeof unsub === 'function') signal.addEventListener('abort', unsub, { once: true });
    }

    /* teardown（section:unload）: close イベントは非同期なので同期で解除する（tol-cart-page.js と同型） */
    signal.addEventListener(
      'abort',
      () => {
        if (dialog.open) dialog.close();
        unlock();
        invalidate();
      },
      { once: true }
    );
  };

  const initPdp = (pdp) => {
    if (pdp.dataset.tolPdpInit) return;
    pdp.dataset.tolPdpInit = 'true';
    const scope = pdp; /* product-info 要素自身がスコープ */
    const abort = new AbortController();
    const signal = abort.signal;
    pdp._tolPdp = { abort };

    initSubscription(scope, signal);
    initPackaging(scope, signal);
    initWishlist(scope, signal);
    initSaleGuard(pdp);
    initCartGuardLabel(pdp); /* R12 層 (c)・ブロック中のラベル復旧（sale ガードと同型） */
    initReservationCta(pdp, scope, signal); /* W5・WS 予約 CTA の enable owner（signal ベース teardown） */
    initStickyBar(pdp, scope, signal); /* G6・SP 追従バー+購入シート（signal ベース teardown） */
    initBtoNotice(pdp, signal); /* #21・受注生産品の案内の variant 追従（signal ベース teardown） */
    initStoreStock(pdp, scope, signal); /* #25・店舗在庫ドロワー（signal ベース teardown） */
  };

  const init = (root) => {
    const pdps = Array.from(root.querySelectorAll('[data-tol-pdp]'));
    if (root.matches && root.matches('[data-tol-pdp]')) pdps.push(root);
    pdps.forEach(initPdp);
  };

  init(document);
  document.addEventListener('shopify:section:load', (e) => init(e.target));
  document.addEventListener('shopify:section:unload', (e) => {
    e.target.querySelectorAll('[data-tol-pdp]').forEach((pdp) => {
      if (pdp._tolPdp) pdp._tolPdp.abort.abort();
      if (pdp._tolSaleUnsub) pdp._tolSaleUnsub(); /* sale ガードの pub/sub 購読解除 */
      if (pdp._tolCartGuardUnsub) pdp._tolCartGuardUnsub(); /* R12 カート手前制御ラベルガードの購読解除 */
      if (pdp._tolPsheet) {
        pdp._tolPsheet.close(); /* 開いたまま破棄で body class / trapFocus 残留を防ぐ（PLP _tolFilters 同型） */
        pdp._tolPsheet.deactivate(); /* 下部余白 class の掃除 */
      }
    });
  });
})();
